import { useTranslation } from 'react-i18next';

import { CONVERT_FORMATS, CONVERT_SIZES, type ConvertFormat, type ConvertSize } from '../media.api';

export interface ConvertOptionsProps {
  format: ConvertFormat | null;
  onFormatChange: (value: ConvertFormat | null) => void;
  size: ConvertSize;
  onSizeChange: (value: ConvertSize) => void;
  /** Count of documents (e.g. PDFs) in the current selection — shown as an informative, never red, notice (h11-3). */
  documentCount: number;
}

/**
 * Format + size conversion controls for a download (h11-3, FR51, mockup dam.html bulkbar
 * "Converteren: PNG ▾ · Afmeting: origineel/1200px/800px"). Strictly the closed FR51 matrix —
 * no free-form dimensions. Conversion itself runs server-side (existing sharp dependency);
 * this component only picks the options.
 */
export function ConvertOptions({ format, onFormatChange, size, onSizeChange, documentCount }: ConvertOptionsProps) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="media-convert-options">
      <select
        aria-label={t('media.library.convert.formatLabel')}
        className="h-9 rounded-[var(--radius-control)] border border-[var(--border)] bg-[var(--background)] px-3 text-sm"
        onChange={(event) => onFormatChange(event.target.value === '' ? null : event.target.value as ConvertFormat)}
        value={format ?? ''}
      >
        <option value="">{t('media.library.convert.noConversion')}</option>
        {CONVERT_FORMATS.map((value) => (
          <option key={value} value={value}>{t(`media.library.convert.formats.${value}`)}</option>
        ))}
      </select>

      {format != null && (
        <select
          aria-label={t('media.library.convert.sizeLabel')}
          className="h-9 rounded-[var(--radius-control)] border border-[var(--border)] bg-[var(--background)] px-3 text-sm"
          onChange={(event) => onSizeChange(event.target.value as ConvertSize)}
          value={size}
        >
          {CONVERT_SIZES.map((value) => (
            <option key={value} value={value}>{t(`media.library.convert.sizes.${value}`)}</option>
          ))}
        </select>
      )}

      {format != null && documentCount > 0 && (
        <span className="text-xs text-[var(--foreground-muted)]" role="status">
          {t('media.library.convert.documentsUnchanged', { count: documentCount })}
        </span>
      )}
    </div>
  );
}
